import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Mic, Loader2 } from "lucide-react";
import { sr } from "../../lib/sr";

// Web Speech API (Chrome/Edge/Safari). Gracefully hidden if unsupported.
const SR_REC = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export default function SrCopilot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [listening, setListening] = useState(false);
  const sessionRef = useRef(null);
  const recRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && suggestions.length === 0) {
      sr.copilotSuggestions().then((r) => setSuggestions(r.suggestions || [])).catch(() => {});
    }
  }, [open, suggestions.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy]);

  const ask = async (question) => {
    const q = (question ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const r = await sr.copilotAsk(q, sessionRef.current);
      sessionRef.current = r.session_id;
      setMsgs((m) => [...m, { role: "assistant", content: r.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry — I couldn't answer that just now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const toggleVoice = () => {
    if (!SR_REC) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR_REC();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.onresult = (e) => { const t = e.results[0][0].transcript; setInput(t); setTimeout(() => ask(t), 150); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="copilot-fab" aria-label="Ask SmartReach"
          style={{ position: "fixed", right: 24, bottom: 24, zIndex: 900, width: 58, height: 58, borderRadius: 999,
            background: "var(--navy)", color: "#fff", border: "none", cursor: "pointer", boxShadow: "0 8px 28px rgba(8,26,58,.35)",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Sparkles size={24} color="var(--gold)" />
        </button>
      )}

      {open && (
        <div data-testid="copilot-panel" style={{ position: "fixed", right: 24, bottom: 24, zIndex: 900, width: 380, maxWidth: "calc(100vw - 32px)",
          height: 560, maxHeight: "calc(100vh - 48px)", background: "#fff", borderRadius: 18, border: "1px solid var(--border)",
          boxShadow: "0 18px 48px rgba(8,26,58,.28)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ background: "var(--navy)", color: "#fff", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Sparkles size={18} color="var(--gold)" />
              <div><div style={{ fontWeight: 700, fontSize: 15 }}>Ask SmartReach</div>
                <div style={{ fontSize: 11, color: "#9fb2d6" }}>Your growth copilot</div></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="copilot-close" aria-label="Close"
              style={{ background: "none", border: "none", color: "#9fb2d6", cursor: "pointer" }}><X size={20} /></button>
          </div>

          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 16, background: "var(--sr-bg,#f6f8fc)" }}>
            {msgs.length === 0 && (
              <div>
                <p style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>Hi! Ask me anything about your growth.</p>
                <p className="sr-muted" style={{ fontSize: 13, marginTop: 4 }}>I know your plan, campaigns, results, offers and loyalty.</p>
                <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => ask(s)} data-testid={`copilot-suggestion-${i}`}
                      style={{ textAlign: "left", background: "#fff", border: "1px solid var(--border)", borderRadius: 12, padding: "10px 12px",
                        fontSize: 13, color: "var(--navy)", cursor: "pointer" }}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 10 }}>
                <div data-testid={`copilot-msg-${m.role}`} style={{ maxWidth: "85%", padding: "10px 12px", borderRadius: 14, fontSize: 13.5, lineHeight: 1.5,
                  whiteSpace: "pre-wrap", background: m.role === "user" ? "var(--blue)" : "#fff",
                  color: m.role === "user" ? "#fff" : "var(--ink,#1e293b)", border: m.role === "user" ? "none" : "1px solid var(--border)" }}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div style={{ display: "flex", gap: 6, alignItems: "center", color: "var(--muted)", fontSize: 13 }}><Loader2 size={15} className="sr-spin" /> Thinking…</div>}
          </div>

          <div style={{ borderTop: "1px solid var(--border)", padding: 10, display: "flex", gap: 8, alignItems: "center", background: "#fff" }}>
            {SR_REC && (
              <button onClick={toggleVoice} data-testid="copilot-voice" aria-label="Voice"
                style={{ background: listening ? "var(--blue)" : "transparent", color: listening ? "#fff" : "var(--muted)",
                  border: "1px solid var(--border)", borderRadius: 999, width: 38, height: 38, cursor: "pointer", flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Mic size={17} />
              </button>
            )}
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder={listening ? "Listening…" : "Ask about your growth…"} data-testid="copilot-input"
              className="sr-input" style={{ flex: 1, padding: "9px 12px" }} />
            <button onClick={() => ask()} disabled={busy || !input.trim()} data-testid="copilot-send" aria-label="Send"
              style={{ background: "var(--navy)", color: "#fff", border: "none", borderRadius: 999, width: 38, height: 38,
                cursor: "pointer", flexShrink: 0, opacity: busy || !input.trim() ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
